import dayjs, { type Dayjs } from '@/utilities/dayjs-config'
import { MealProgramSubscription } from './meal-program-subscription'
import type { Subscription as PopsSubscription } from '@/types/pops'
import {
  $allowedChangesForWeeks,
  $campaignWeeks,
  $mealSwaps,
} from '@/stores/meal-program-subscription/stores'
import { Meal } from './meal'
import type MealSwap from './meal-swap'
import type { DeliveryZone, DeliveryZoneException } from './delivery-zone'
import {
  isWeekAfterCutoff,
  isWeekTooFarForMealSwaps,
  isWeekTooFarToModify,
} from '@/utilities/sakara'
import { $getMenu } from '@/stores/menu/computed'

const DEFAULT_ALLOWED_CHANGES = {
  sku: true,
  campaigns: true,
  systems: true,
  credits: true,
  addons: true,
  wp_destroyable: true,
  swaps: true,
  address: true,
  supplements: true,
  delivery_notes: true,
  meal_modifications: true,
  start_day: true,
  delivery_window: true,
  skipped_week: false,
  skippable: true,
}

type DeliveryForWeek = {
  date: Dayjs
  mealCodes: string[]
  day: string
}

type MealSwapMeal = {
  swapFromMeal?: Meal
  swapToMeal?: Meal
}

export class MealProgramSubscriptionWeek extends MealProgramSubscription {
  skip: 'Yes' | 'No'
  week: dayjs.Dayjs
  #deliveryZone?: DeliveryZone | DeliveryZoneException
  #sakaraClassicMealSwaps?: MealSwap[]
  #mealSwapsMeals?: MealSwapMeal[]
  #deliveriesForWeek?: DeliveryForWeek[]
  #mealDates?: Dayjs[]
  #mealDays?: string[]
  #mealCodes?: string[]
  #upcomingDeliveries?: DeliveryForWeek[]
  #isSkipped?: boolean
  #isUpcomingWeekToBeBilled?: boolean
  #isAfterCutoff?: boolean
  #isFirstWeekOfSubscription?: boolean
  #isTooFarToModify?: boolean
  #isTooFarForMealSwaps?: boolean
  #hasBeenBilled?: boolean

  constructor(rawSubscription: PopsSubscription) {
    super(rawSubscription)

    this.skip = rawSubscription.skip
    this.week = dayjs(rawSubscription.week)
  }

  get deliveryZone() {
    if (this.#deliveryZone) return this.#deliveryZone

    this.#deliveryZone = super.deliveryZone?.getDeliveryZoneForWeek(this.week)

    return this.#deliveryZone
  }

  get campaignWeek() {
    return $campaignWeeks.get()[this.week.format('YYYY-MM-DD')]
  }

  get mealSwaps() {
    return $mealSwaps.get()[this.week.format('YYYY-MM-DD')] || []
  }

  get allowedChanges() {
    return (
      $allowedChangesForWeeks.get()[this.week.format('YYYY-MM-DD')] ||
      DEFAULT_ALLOWED_CHANGES
    )
  }

  get sakaraClassicMealSwaps(): MealSwap[] {
    if (this.#sakaraClassicMealSwaps) return this.#sakaraClassicMealSwaps

    this.#sakaraClassicMealSwaps = this.mealSwaps.filter(
      (mealSwap) => mealSwap.classicMeal,
    )

    return this.#sakaraClassicMealSwaps
  }

  get mealSwapsMeals() {
    if (this.#mealSwapsMeals) return this.#mealSwapsMeals
    if (!this.mealSwaps.length || !this.availableMenu) return []

    this.#mealSwapsMeals = this.mealSwaps.map((mealSwap) => {
      const swapFromMeal = this.availableMenu.getMealByMealCode(
        mealSwap.swapFromMealCode,
      )
      const swapToMeal = this.availableMenu.getMealByMealCode(
        mealSwap.swapToMealCode,
      )

      return {
        swapFromMeal,
        swapToMeal,
      }
    })

    return this.#mealSwapsMeals
  }

  /**
   *
   * @returns Deliveries for week
   * @example
   * For the Inner NYC delivery zone (4 days of food, Breakfast, Dinner):
   *
   * Will return:
   * [
   * 	{
   * 		"date": dayjs(),
   * 		"mealCodes": [
   * 			"B1", "D1", "B2", "D2", "B3", "D3"
   * 		],
   * 		"day": "Sunday"
   * 	},
   * 	{
   * 		"date": dayjs(),
   * 		"mealCodes": [
   * 			"B4", "D4"
   * 		],
   * 		"day": "Wednesday"
   * 	}
   * ]
   */
  get deliveriesForWeek() {
    if (this.#deliveriesForWeek) return this.#deliveriesForWeek

    this.#deliveriesForWeek = this.deliveries.map(({ day, mealCodes }) => ({
      date: dayjs(this.week).day(dayjs.weekdays().indexOf(day)),
      mealCodes,
      day,
    }))

    return this.#deliveriesForWeek
  }

  get mealDates() {
    if (this.#mealDates) return this.#mealDates

    this.#mealDates = this.buildMealDates()

    return this.#mealDates
  }

  get mealDays() {
    if (this.#mealDays) return this.#mealDays

    this.#mealDays = this.mealDates.map((d) => d.format('dddd'))

    return this.#mealDays
  }

  get mealCodes() {
    if (this.#mealCodes) return this.#mealCodes

    this.#mealCodes = this.deliveriesForWeek
      .map((delivery) => delivery.mealCodes)
      .flat()

    return this.#mealCodes
  }

  /**
   * Gets deliveries for this week that have not passed.
   */
  get upcomingDeliveries() {
    if (this.#upcomingDeliveries) return this.#upcomingDeliveries

    const now = dayjs()
    this.#upcomingDeliveries = this.deliveriesForWeek.filter((delivery) => {
      const deliveryDate = dayjs(delivery.date)
      const [hour, minute] = this.deliveryWindow[1].split(':')

      deliveryDate.set('hour', Number(hour)).set('minute', Number(minute))

      return deliveryDate.isAfter(now)
    })

    return this.#upcomingDeliveries
  }

  get upcomingDelivery() {
    return this.upcomingDeliveries[0]
  }

  get availableMenu() {
    return $getMenu.get()(this.week, this.shippingAddress.zip as string)
  }

  get isActive() {
    return !this.isSkipped
  }

  get isSkipped() {
    if (this.#isSkipped) return this.#isSkipped

    this.#isSkipped = this.skip === 'Yes'

    return this.#isSkipped
  }

  get isSkippable() {
    return (
      !this.campaignWeek && this.isModifiable && this.allowedChanges.skippable
    )
  }

  /**
   * Will return true if this week is the next week that is
   * scheduled to be billed. Note: If the week is skipped/not active,
   * this will return true even though the customer will not be billed.
   */
  get isUpcomingWeekToBeBilled() {
    if (this.#isUpcomingWeekToBeBilled) return this.#isUpcomingWeekToBeBilled

    this.#isUpcomingWeekToBeBilled = this.upcomingWeekToBeBilled.isSame(
      this.week,
      'week',
    )

    return this.#isUpcomingWeekToBeBilled
  }

  get isAfterCutoff() {
    if (this.#isAfterCutoff) return this.#isAfterCutoff

    this.#isAfterCutoff = this.isWeekAfterCutoff(this.week)

    return this.#isAfterCutoff
  }

  get isFirstWeekOfSubscription() {
    if (this.#isFirstWeekOfSubscription) return this.#isFirstWeekOfSubscription

    this.#isFirstWeekOfSubscription = this.isWeekFirstWeekOfSubscription(
      this.week,
    )

    return this.#isFirstWeekOfSubscription
  }

  get isTooFarToModify() {
    if (this.#isTooFarToModify) return this.#isTooFarToModify

    this.#isTooFarToModify = this.isWeekTooFarToModify(this.week)

    return this.#isTooFarToModify
  }

  get isTooFarForMealSwaps() {
    if (this.#isTooFarForMealSwaps) return this.#isTooFarForMealSwaps

    this.#isTooFarForMealSwaps = isWeekTooFarForMealSwaps(this.week)

    return this.#isTooFarForMealSwaps
  }

  get isModifiable() {
    return (
      this.isAfterCutoff &&
      this.allowedChanges.meal_modifications &&
      !this.isTooFarToModify
    )
  }

  get isSwapCapReached() {
    return this.mealSwaps.length >= this.totalMealSwaps
  }

  get isOptedIn() {
    return !!this.campaignWeek
  }

  get isCleanseWeek() {
    return (
      this.campaignWeek &&
      (this.campaignWeek.isDetoxPlusWeek ||
        this.campaignWeek.campaign.isCleanse)
    )
  }

  get isFixedCampaignWeek() {
    return !!this.campaignWeek?.campaign?.isFixed
  }

  get isSummerCampaignWeek() {
    return !!this.campaignWeek?.campaign?.isSummerCampaign
  }

  get hasBeenBilled() {
    if (this.#hasBeenBilled) return this.#hasBeenBilled

    this.#hasBeenBilled = !this.isAfterCutoff && this.isActive

    return this.#hasBeenBilled
  }

  get hasUpcomingDeliveries() {
    return this.upcomingDeliveries.length > 0
  }

  isMealDay(day: Dayjs) {
    return this.mealDays.includes(day.format('dddd'))
  }

  isMealCodeSwapped(mealCode: string) {
    return this.mealSwaps.some(
      (mealSwap) => mealSwap.swapFromMealCode === mealCode,
    )
  }

  isMealSwapped(meal: Meal) {
    return this.isMealCodeSwapped(meal.mealCode)
  }

  isWeekAfterCutoff(week: Dayjs) {
    return isWeekAfterCutoff(week)
  }

  isWeekModifiable(week: Dayjs) {
    return (
      this.isWeekAfterCutoff(week) &&
      !this.isWeekFirstWeekOfSubscription(week) &&
      !this.isWeekTooFarToModify(week)
    )
  }

  isWeekFirstWeekOfSubscription(week: Dayjs) {
    return week.isSame(this.startDate, 'week')
  }

  isWeekTooFarToModify(week: Dayjs) {
    return isWeekTooFarToModify(week)
  }

  getMealSwapByMealCode(mealCode: string) {
    return this.mealSwaps.find(
      (mealSwap) => mealSwap.swapFromMealCode === mealCode,
    )
  }

  getSwappedMeal(meal: Meal) {
    const mealSwap = this.getMealSwapByMealCode(meal.mealCode)

    if (!mealSwap || !this.availableMenu) return null

    return this.availableMenu.getMealByMealCode(mealSwap.swapToMealCode)
  }

  getSwappableMeals(meal: Meal) {
    if (!this.deliveryZone) return null

    const { totalDeliverableMeals } = this.deliveryZone

    // Get the possible shipments for the current delivery zone
    // TODO: Refactor so that DeliveryZone.deliveries has what we need
    const deliveries = this.deliveryZone.getDeliveriesForDaysOfFood(
      totalDeliverableMeals,
    )

    // Get the delivery/shipment that contains the meal to be swapped
    const deliveryIndex = deliveries.findIndex((delivery) =>
      delivery.mealCodes.includes(meal.mealCode),
    )

    // No delivery found with the given meal
    if (deliveryIndex === -1) return []

    const delivery = deliveries[deliveryIndex]

    // The delivery cycle is a number representing a shipment in the week (ie. 1 is the first shipment)
    const deliveryCycle = deliveryIndex + 1

    const meals = this.availableMenu
      .getMealsByMealCodes(delivery.mealCodes)
      .filter((availableMeal) => meal.isSwappableWith(availableMeal as Meal))

    const sakaraClassicMeals = this.availableMenu
      .getSakaraClassicMeals(deliveryCycle)
      .filter((availableMeal) => meal.isSwappableWith(availableMeal))

    return {
      meals,
      sakaraClassicMeals,
    }
  }

  buildMealDates() {
    return this.mealCodes.reduce((mealDates, mealCode) => {
      const mealDate = Meal.getDateFromMealCode(mealCode, this.week)

      if (!mealDates.some((d) => d.isSame(mealDate, 'day'))) {
        mealDates.push(mealDate)
      }

      return mealDates
    }, [] as Dayjs[])
  }
}
